import { queryClient } from "@/src/config/react-query";
import {
  GroupMemberRole,
  JoinType,
  MembershipStatus,
} from "@/src/constants/groups/group-membership";
import { GroupDetailsTab } from "@/src/constants/groups/group-tabs";
import { InvitableMember, UserProfile } from "@/src/models/user.model";
import {
  useCreateGroupMembership,
  useCreateGroupMembershipsBulk,
  useDeleteGroup,
  useGetGroupById,
  useGetGroupMemberCount,
  useGetGroupMemberIds,
  useGetLatestGroups,
  useGetMyMembership,
  useRemoveGroupMembership,
  useUpdateGroupMemberCount,
  useUpdateGroupMembership,
} from "@/src/services/api/groups/groups.hooks";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/src/contexts/auth.context";

export const useGroupDetails = (id?: string, user?: UserProfile) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();

  // Group Data
  const { data, isPending } = useGetGroupById(id as string);
  const { data: memberCount = 0 } = useGetGroupMemberCount(id as string);
  const { data: existingMemberIds = [] } = useGetGroupMemberIds(id as string);
  const group = data
    ? {
        id: data.ItemId,
        name: data.name,
        description: data.description,
        coverImageUrl: data.coverImageUrl,
        profileImageUrl: data.profileImageUrl,
        avatarLabel: data.name?.slice(0, 2).toUpperCase() ?? "NA",
        isPrivate: data.isPrivate ?? false,
        memberCount: memberCount,
        admin: data.CreatedBy,
      }
    : undefined;

  // Membership
  const { data: myMembership } = useGetMyMembership(
    id as string,
    user?.itemId ?? "",
  );
  const isMember = !!myMembership;
  const status = myMembership?.status;
  const isAdmin =
    myMembership?.role === GroupMemberRole.ADMIN ||
    group?.admin === user?.itemId;

  /**
   * Whether the group's tab bodies — posts, members, events, files, news — may
   * be rendered.
   *
   * Two things were wrong before. Each tab tested `isMember && status ===
   * "active"` for itself, and the News tab simply forgot to, so a non-member
   * could read a private group's news while every other tab stayed blank. And a
   * super-admin, who moderates these groups and can already delete them, was
   * held out of every tab of a group they had not joined. One flag now decides
   * it for the whole screen.
   */
  const canViewContent = isSuperAdmin || (isMember && status === "active");

  // Tabs
  const [activeTab, setActiveTab] = useState<GroupDetailsTab>(
    GroupDetailsTab.POSTS,
  );
  const handleTabChange = (tab: GroupDetailsTab) => setActiveTab(tab);

  // Latest groups (replaces mock suggestions) for the right-panel sidebar
  const { data: latestGroups = [] } = useGetLatestGroups(5);
  const handleSuggestion = (suggestionId: string) => {
    router.push(`/groups/${suggestionId}`);
  };

  // Toast
  const [toast, setToast] = useState<{
    text1: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return;
    const timer = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Mutations
  const { mutateAsync: createGroupMembership } = useCreateGroupMembership();
  const { mutateAsync: createGroupMembershipsBulk } =
    useCreateGroupMembershipsBulk();
  const { mutateAsync: updateGroupMembership } = useUpdateGroupMembership();
  const { mutateAsync: updateMemberCount } = useUpdateGroupMemberCount();
  const { mutateAsync: removeGroupMembership } = useRemoveGroupMembership();
  const { mutateAsync: deleteGroup } = useDeleteGroup();
  const { mutate: sendNotification } = useSendNotification();
  const [isActionPending, setIsActionPending] = useState(false);

  // Add members modal
  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<InvitableMember[]>([]);

  const handleJoinGroup = async () => {
    if (!user?.itemId || !id || isActionPending) return;
    setIsActionPending(true);
    try {
      // A super-admin is not a petitioner: they can already read, moderate and
      // delete any group, so routing them through a request an admin must
      // approve is ceremony that grants nothing. They join outright, private or
      // not, and no approval notification goes out.
      const needsApproval = !!group?.isPrivate && !isSuperAdmin;

      const membership = await createGroupMembership({
        groupId: id,
        userId: user.itemId,
        role: GroupMemberRole.MEMBER,
        status: needsApproval
          ? MembershipStatus.PENDING
          : MembershipStatus.ACTIVE,
        joinType: needsApproval ? JoinType.REQUEST : JoinType.DIRECT_JOIN,
        groupUserSnapshot: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          profileImageUrl: user.profileImageUrl,
        },
        joinedAt: new Date().toLocaleDateString("en-GB"),
      });

      // Notify group admin about join request (private groups only)
      if (needsApproval && group?.admin) {
        sendNotification({
          userIds: [group.admin],
          denormalizedPayload: JSON.stringify({
            type: "join_request",
            message: t("NOTIFY_JOIN_REQUEST", {
              firstName: user.firstName,
              lastName: user.lastName,
              groupName: group.name,
            }),
            actorAvatar: user.profileImageUrl,
            actorName: `${user.firstName} ${user.lastName}`,
            actorId: user.itemId,
            groupId: id,
            membershipId: membership?.insertgroupmembership?.itemId,
            targetEntityId: id,
          }),
          configurationName: "join-request",
          responseKey: "group-action",
          responseValue: "join_request",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      // Joining widens what the main feed may show: `userGroups` (read from these
      // membership rows) gates group posts there, so both it and the feed's own
      // cache have to be dropped or the new group's posts stay hidden until the
      // next cold start.
      queryClient.invalidateQueries({ queryKey: ["getUserGroupMemberships"] });
      queryClient.invalidateQueries({ queryKey: POSTS_QUERY_KEY });
      setToast({
        text1: t(needsApproval ? "LABEL_REQUEST_SENT" : "LABEL_JOINED"),
        type: "success",
      });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleAddMembers = async (members: InvitableMember[]) => {
    if (!id || isActionPending) return;
    setIsActionPending(true);
    try {
      // One request for the whole selection. This was N concurrent single inserts
      // in a Promise.all — fine for a handful, but a country filter can select
      // hundreds, and a failure part-way through left no record of which
      // memberships had been created. The bulk mutation reports
      // `totalImpactedData`, and the hook rejects on a short write, so a partial
      // add surfaces instead of the UI reporting success.
      //
      // Field placement is unchanged: role/status/joinType/joinedAt stay at the
      // top level, which is where use-group-members-table already reads them.
      const joinedAt = new Date().toLocaleDateString("en-GB");
      await createGroupMembershipsBulk(
        members.map((member) => ({
          groupId: id,
          userId: member.itemId,
          role: GroupMemberRole.MEMBER,
          status: MembershipStatus.ACTIVE,
          joinType: JoinType.ADDED_BY_ADMIN,
          groupUserSnapshot: {
            userId: member.itemId,
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
            displayName: member.displayName,
            profileImageUrl: member.profileImageUrl,
          },
          joinedAt,
        })),
      );

      // Notify each added member
      if (members.length > 0) {
        sendNotification({
          userIds: members.map((m) => m.itemId),
          denormalizedPayload: JSON.stringify({
            type: "added_to_group",
            message: t("NOTIFY_ADDED_TO_GROUP", {
              firstName: user?.firstName,
              lastName: user?.lastName,
              groupName: group?.name,
            }),
            actorAvatar: user?.profileImageUrl,
            actorName: `${user?.firstName} ${user?.lastName}`,
            actorId: user?.itemId,
            groupId: id,
            targetEntityId: id,
          }),
          // Was "post-liked" — a copy-paste that published group adds on the
          // postLiked channel. The tenant defines `add-to-group`
          // (notifyMethod: addToGroup), which is what this event is, and it is
          // already declared in SENT_CONFIGURATION_NAMES.
          configurationName: "add-to-group",
          responseKey: "group-action",
          responseValue: "added_to_group",
        });
      }

      await updateMemberCount(id);
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      setInvitedMembers([]);
      setToast({ text1: t("LABEL_MEMBERS_ADDED"), type: "success" });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const addMembership = async (membershipId: string) => {
    try {
      setIsActionPending(true);
      await updateGroupMembership({
        itemId: membershipId,
        status: MembershipStatus.ACTIVE,
      });
      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.refetchQueries({ queryKey: ["getGroupMembers", id] });
      setToast({
        text1: t("LABEL_MEMBER_ADDED"),
        type: "success",
      });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleRemoveMembership = async (
    membershipId: string,
    toastKey: string = "LABEL_GROUP_LEFT",
    removedUserId?: string,
  ) => {
    if (!membershipId || isActionPending) return;
    try {
      setIsActionPending(true);
      await removeGroupMembership(membershipId);

      // Notify removed user (skip if they left themselves)
      if (removedUserId && removedUserId !== user?.itemId) {
        sendNotification({
          userIds: [removedUserId],
          denormalizedPayload: JSON.stringify({
            type: "removed_from_group",
            message: t("NOTIFY_REMOVED_FROM_GROUP", {
              groupName: group?.name,
            }),
            actorAvatar: user?.profileImageUrl,
            actorName: `${user?.firstName} ${user?.lastName}`,
            actorId: user?.itemId,
          }),
          configurationName: "remove-from-group",
          responseKey: "group-action",
          responseValue: "removed_from_group",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount", id] });
      queryClient.invalidateQueries({ queryKey: ["getMyMembership", id] });
      queryClient.invalidateQueries({ queryKey: ["getAllMyMemberships", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMembers", id] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds", id] });
      queryClient.invalidateQueries({ queryKey: ["getPendingMembers", id] });
      // Mirror of the join path: leaving narrows the feed, so drop the
      // membership list and the feed cache that is filtered against it.
      queryClient.invalidateQueries({ queryKey: ["getUserGroupMemberships"] });
      queryClient.invalidateQueries({ queryKey: POSTS_QUERY_KEY });
      setToast({ text1: t(toastKey), type: "success" });
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!id || isActionPending) return;
    try {
      setIsActionPending(true);
      await deleteGroup(id);

      queryClient.invalidateQueries({ queryKey: ["getGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getLatestGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getUserGroupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["getAllMyMemberships"] });
      queryClient.invalidateQueries({ queryKey: POSTS_QUERY_KEY });

      // Group no longer exists — return to the groups list.
      router.replace("/groups");
    } catch {
      setToast({ text1: t("ERROR_SERVER"), type: "error" });
    } finally {
      setIsActionPending(false);
    }
  };

  return {
    group,
    isPending,

    isAdmin,
    isMember,
    isSuperAdmin,
    canViewContent,
    myMembership,
    status,

    activeTab,
    handleTabChange,

    latestGroups,
    handleSuggestion,

    toast,
    setToast,

    addMembership,
    handleJoinGroup,
    handleRemoveMembership,
    handleDeleteGroup,
    isActionPending,

    invitedMembers,
    existingMemberIds,
    membersModalVisible,
    setMembersModalVisible,
    handleAddMembers,
  };
};
